// iOS-only first-launch onboarding.
//
// Shown to anyone opening the native Capacitor iOS app who is NOT logged
// in. Web users never see this component; they still use the marketing
// Landing page + Stripe quiz flow.
//
// State machine: language → slides → signup → /quiz (existing) → /results
// → NativePaywall (existing) → app unlocked.
//
// Design contract
// ---------------
// * Single-component state machine — no external routing between steps so
//   users can't hit back-button-into-a-broken-state.
// * Language choice persists in localStorage under `calofay_lang_v1`.
//   Once set, this whole component is bypassed permanently and the app
//   boots straight to the user's language.
// * All 3 marketing slides are self-contained (no external Swiper lib) —
//   we swap between them with a state index and a translate-x transform.
// * Signup uses the existing `/api/auth/signup` endpoint — the same one
//   the web quiz calls after Stripe payment — so no backend change needed.
//   The account is created immediately, gated behind SubscriptionGate
//   until the user hits the paywall after the quiz.

import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Sparkles, Camera, Brain, Trophy, Eye, EyeOff, Zap, Heart, Star, Award, Utensils, Activity, Target } from "lucide-react";
import { toast } from "sonner";
import api, { saveSession } from "@/lib/api";
import { saveRememberedCreds } from "@/lib/remember";
import { useAuth } from "@/lib/auth";
import { isNativeApp, openExternal } from "@/lib/native";
import { useI18n } from "@/lib/i18n.jsx";
import { attributionForApi } from "@/lib/attribution";
import CountryWelcome from "@/components/CountryWelcome";
import OnboardingFeaturesScreen from "@/components/OnboardingFeaturesScreen";
import OnboardingCompetitorsScreen from "@/components/OnboardingCompetitorsScreen";
import OnboardingTestimonialsScreen from "@/components/OnboardingTestimonialsScreen";
import OnboardingTrialPromiseScreen from "@/components/OnboardingTrialPromiseScreen";
import OnboardingAndroidPromoScreen from "@/components/OnboardingAndroidPromoScreen";
import OnboardingGuaranteeScreen from "@/components/OnboardingGuaranteeScreen";
import OnboardingWaterCompareScreen from "@/components/OnboardingWaterCompareScreen";
import { Capacitor } from "@capacitor/core";
import { shouldShowCountryWelcome } from "@/lib/gulfCountry";

// Legacy iOS-only key. We now delegate to the global i18n provider
// (which uses `calofay:lang`) so the Quiz + all downstream screens see
// the same language the user picked here. Still write to LANG_KEY on
// pick to keep older builds cache-compatible during rolling updates.
const LANG_KEY = "calofay_lang_v1";
const ONBOARDING_DONE_KEY = "calofay_onboarding_v1";

const COPY = {
  ar: {
    // Language screen
    pickLanguage: "اختر اللغة",
    pickSubtitle: "يمكنك تغييرها لاحقاً من الإعدادات",
    continue: "متابعة",
    // Slides (v2 — 5 hero-image slides). Server-driven, but these
    // strings act as the offline fallback if `/onboarding/slides` fails.
    slide1Title: "هدفك أقرب مما تتخيل مع تطبيق كالوفاي",
    slide1Body: "خذ خطوة بسيطة تقربك من النتيجة اللي تبغاها.",
    slide2Title: "سجل أكلك بالطريقة اللي تناسبك",
    slide2Body: "صورة، باركود، توصف وجبتك للذكاء اصطناعي، أو تضيف وجبتك يدوي.",
    slide3Title: "اعرف وش يحتاج جسمك بالضبط",
    slide3Body: "سعرات وماكروز مناسبة لجسمك وهدفك.",
    slide4Title: "تمارينك تقربك لهدفك",
    slide4Body: "شوف السعرات اللي حرقتها وكيف تساعدك توصل لهدفك.",
    slide5Title: "العادات الصغيرة تصنع فرق كبير",
    slide5Body: "تابع خطواتك وموية يومك في مكان واحد.",
    slide6Title: "شوف تقدمك بوضوح",
    slide6Body: "تابع سعراتك الداخلة من الأكل، والمحروقة من التمارين، وتقدمك اليومي.",
    slide7Title: "تابع تغير جسمك",
    slide7Body: "تابع وزنك وصور تقدمك مع الوقت.",
    slide8Title: "مدرب الذكاء الإصطناعي معك دائمًا",
    slide8Body: "اسأل شات الذكاء الإصطناعي عن الأكل والتمارين والوصفات بأي وقت.",
    slide9Title: "مو لازم تكمل لحالك لو احتجت دعم",
    slide9Body: "تواصل مع أخصائية تغذية عبر واتساب اذا عندك سؤال أو استشارة.",
    slide10Title: "ابدأ اليوم، وشوف الفرق مع الوقت",
    slide10Body: "خطوات بسيطة تصنع نتائج حقيقية بإذن الله.",
    getStarted: "ابدأ رحلتك مع تطبيق كالوفاي",
    haveAccount: "لدي حساب بالفعل",
    // Signup
    signupTitle: "أنشئ حسابك",
    signupSubtitle: "خطوة واحدة لتبدأ رحلتك",
    emailLabel: "البريد الإلكتروني",
    emailPlaceholder: "you@example.com",
    passwordLabel: "كلمة السر",
    passwordPlaceholder: "على الأقل 8 أحرف",
    signupCta: "متابعة",
    signupTerms: "بمتابعتك، أنت توافق على شروط الخدمة وسياسة الخصوصية، بما في ذلك استخدامنا لخدمات ذكاء اصطناعي من طرف ثالث (OpenAI و Google Gemini) لتحليل صور الوجبات وتوليد ردود المدرّب.",
    alreadyHaveAccount: "لدي حساب؟",
    loginLink: "سجل دخول",
    signupError: "تعذر إنشاء الحساب. حاول مرة أخرى.",
    // Skip
    skip: "تخطي",
  },
  en: {
    pickLanguage: "Choose Language",
    pickSubtitle: "You can change this later in Settings",
    continue: "Continue",
    slide1Title: "Your Goal Is Closer Than You Think",
    slide1Body: "Take one simple step toward the results you want.",
    slide2Title: "Track Meals Your Way",
    slide2Body: "Photo, barcode, AI, or manual entry. No more guessing.",
    slide3Title: "Know Exactly What Your Body Needs",
    slide3Body: "Get calorie and macro goals made for you.",
    slide4Title: "Your Workouts Bring You Closer to Your Goal",
    slide4Body: "See the calories you burn and how they help you reach your goal.",
    slide5Title: "Small Habits Create Big Changes",
    slide5Body: "Track your steps and water in one place.",
    slide6Title: "See Your Progress Clearly",
    slide6Body: "See calories in, calories out, and your daily progress.",
    slide7Title: "See Changes Beyond the Scale",
    slide7Body: "Track your weight and progress photos over time.",
    slide8Title: "Your AI Chat Coach Is Always With You",
    slide8Body: "Get instant help with food, workouts, and motivation.",
    slide9Title: "You Are Not Doing This Alone",
    slide9Body: "Have a question? Our support team answers on WhatsApp.",
    slide10Title: "Start Today and See the Difference Over Time",
    slide10Body: "Small steps create real results.",
    getStarted: "Start Your Journey With Calofay",
    haveAccount: "I have an account",
    signupTitle: "Create Your Account",
    signupSubtitle: "One step to start your journey",
    emailLabel: "Email",
    emailPlaceholder: "you@example.com",
    passwordLabel: "Password",
    passwordPlaceholder: "At least 8 characters",
    signupCta: "Continue",
    signupTerms: "By continuing you agree to the Terms of Service and Privacy Policy, including our use of third-party AI services (OpenAI and Google Gemini) to analyze meal photos and generate coach responses.",
    alreadyHaveAccount: "Have an account?",
    loginLink: "Log in",
    signupError: "Could not create account. Please try again.",
    skip: "Skip",
  },
};

/** True after user has completed onboarding (or is already logged in). */
export function isOnboardingComplete() {
  try {
    return localStorage.getItem(ONBOARDING_DONE_KEY) === "1";
  } catch {
    return false;
  }
}

export function markOnboardingComplete() {
  try { localStorage.setItem(ONBOARDING_DONE_KEY, "1"); } catch {}
}

export function getPreferredLang() {
  // Check the global i18n key first (source of truth used by the
  // rest of the app), then the legacy iOS-only key for users who
  // installed an earlier build.
  try {
    return (
      localStorage.getItem("calofay:lang") ||
      localStorage.getItem(LANG_KEY) ||
      null
    );
  } catch { return null; }
}

export default function IOSOnboarding() {
  const nav = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();

  // React-Router-aware route detection. We deliberately do NOT use
  // window.location.pathname — when navigating between the two iOS
  // routes (/ios/welcome ↔ /ios/signup) React reuses the same
  // component instance and the useState initializer only runs on the
  // *first* mount, so a stale `window.location` snapshot leaks in
  // and the component thinks it's still on the welcome route → shows
  // the language picker again. `location.pathname` from useLocation()
  // updates on every route change and drives the effect below to
  // reset the step accordingly.
  const isSignupRoute = location.pathname.endsWith("/signup");

  const initialLang = getPreferredLang();
  // Delegate language state to the global provider so downstream
  // pages (Quiz, Results, NativePaywall) render in the SAME language
  // the user picked here. Previously we kept a private `lang` state
  // and localStorage key (`calofay_lang_v1`), which meant picking
  // English on the language screen only changed the 3 slides —
  // Quiz.jsx read from `useI18n()` and stayed on Arabic.
  const { lang: globalLang, setLang: setGlobalLang } = useI18n();
  const [step, setStep] = useState(
    // Signup route now lands on "testimonials" first (final trust nudge
    // before Create-Your-Account per owner Sep 30 2026). Testimonials'
    // Continue button transitions to "signup" without touching the URL.
    isSignupRoute ? "testimonials" : (initialLang ? "slides" : "language")
  );

  // Sub-step inside `trial-promise` for Stripe-flow users (Android +
  // iOS-GCC): "guarantee" shows the 15-day money-back page first, then
  // "code" shows the N10 promo page. Keeps the outer step machine
  // untouched so the sub-step resets naturally if the user navigates
  // back and re-enters this step.
  const [promoSubStep, setPromoSubStep] = useState("guarantee");

  // If the user landed here with a pre-existing legacy choice but the
  // global provider hasn't caught up yet, mirror it up on mount.
  useEffect(() => {
    if (initialLang && initialLang !== globalLang) {
      setGlobalLang(initialLang);
    }
    // Intentionally run once — sync only at mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lang = globalLang;

  // Keep step in sync with the URL whenever the user bounces between
  // /ios/welcome and /ios/signup at runtime. Without this, arriving at
  // /ios/signup from /results would still show whichever step the
  // state machine last landed on.
  useEffect(() => {
    if (isSignupRoute) setStep("testimonials");
    else if (getPreferredLang()) setStep("slides");
    else setStep("language");
  }, [isSignupRoute]);

  const t = COPY[lang];
  const isRTL = lang === "ar";

  // Persist language choice + apply <html> direction immediately
  useEffect(() => {
    try {
      document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
      document.documentElement.setAttribute("lang", lang);
    } catch {}
  }, [lang, isRTL]);

  // GCC-Stripe gateway pre-fetch for native iOS. SubscriptionGate also
  // populates this cache the first time it mounts on `/app`, but during
  // signup the user hits the `trial-promise` step BEFORE reaching
  // `/app`, so without this preload the first-ever signup for a GCC
  // user would fall through to the Apple-IAP TrialPromise screen
  // instead of the new Guarantee → Code → Stripe sequence. Non-iOS
  // and users who already have the cached decision are no-ops.
  useEffect(() => {
    let plat;
    try { plat = Capacitor.getPlatform(); } catch { plat = "web"; }
    if (plat !== "ios") return;
    try {
      if (localStorage.getItem("calofay:gcc_gateway_cache_v1")) return;
    } catch {}
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/geo/paywall-decision");
        if (!alive) return;
        const gateway = data?.gateway === "stripe" ? "stripe" : "apple";
        try {
          localStorage.setItem(
            "calofay:gcc_gateway_cache_v1",
            JSON.stringify({ gateway, country: data?.country, at: Date.now() }),
          );
        } catch (_) {}
      } catch (_) {
        // Silent — SubscriptionGate will try again on /app mount, and
        // if that also fails we fail-safe to Apple IAP so an Apple
        // reviewer never gets routed through Stripe by accident.
      }
    })();
    return () => { alive = false; };
  }, []);

  const pickLanguage = (l) => {
    // Update BOTH the global i18n provider (drives Quiz + rest of app)
    // and the legacy iOS key (backwards-compat for older cached JS).
    setGlobalLang(l);
    try { localStorage.setItem(LANG_KEY, l); } catch {}
    // Gulf-country welcome screen slots in RIGHT after language.
    // Only shows once per install and only for SA/AE/QA/KW/OM
    // (detected via IANA timezone — no network, no permission).
    // Everyone else falls straight through to the slides.
    setStep(shouldShowCountryWelcome() ? "country" : "slides");
  };

  // ------------------------------------------------------------------
  // Screen: Country welcome (Gulf-only, first-launch only)
  // ------------------------------------------------------------------
  if (step === "country") {
    return <CountryWelcome onContinue={() => setStep("slides")} langOverride={lang} />;
  }

  // ------------------------------------------------------------------
  // Screen: Language picker
  // ------------------------------------------------------------------
  if (step === "language") {
    return (
      <div
        className="always-light bg-white flex flex-col px-6 overflow-hidden"
        style={{ height: "calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom))", paddingTop: "24px", paddingBottom: "24px" }}
        data-testid="ios-onboard-language"
      >
        <div className="flex-1 flex flex-col items-center justify-center gap-2 min-h-0">
          {/* Real Calofay app icon — same one used inside the quiz intro
              hero. Replaces the placeholder Sparkles gradient tile so
              the very first screen the user sees looks native, not
              generic. The image is served as a 19 KB WebP that loads
              instantly on cellular. `loading="eager"` because this is
              the primary above-the-fold hero on a first-launch screen. */}
          <div className="w-24 h-24 rounded-[28px] overflow-hidden shadow-floating mb-4">
            <img
              src="/calofay-mark.webp"
              alt="Calofay"
              className="w-full h-full object-cover"
              draggable={false}
              loading="eager"
              fetchpriority="high"
              data-testid="ios-lang-logo"
            />
          </div>
          <h1 className="text-3xl font-black text-neutral-900">Calofay</h1>
          <p className="text-sm text-neutral-500 mt-1">Choose your language</p>
        </div>

        <div className="space-y-3">
          <button
            onClick={() => pickLanguage("ar")}
            className="w-full rounded-2xl border-2 border-brand-500 bg-brand-50 py-4 text-lg font-bold text-brand-700 hover:bg-brand-100 transition-colors flex items-center justify-center gap-3"
            data-testid="ios-lang-ar"
          >
            <span className="text-2xl">🇸🇦</span>
            <span>العربية</span>
          </button>
          <button
            onClick={() => pickLanguage("en")}
            className="w-full rounded-2xl border-2 border-neutral-200 bg-white py-4 text-lg font-bold text-neutral-800 hover:border-brand-300 hover:bg-neutral-50 transition-colors flex items-center justify-center gap-3"
            data-testid="ios-lang-en"
          >
            <span className="text-2xl">🇬🇧</span>
            <span>English</span>
          </button>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------------
  // Screen: Slides
  // ------------------------------------------------------------------
  if (step === "slides") {
    // "Get Started" from the last slide moves users into the two new
    // marketing preview screens (features → competitors), then into
    // the anonymous quiz. After the quiz + results screens, Quiz.jsx
    // routes back to /ios/signup which re-renders this component in
    // signup mode.
    return (
      <SlidesScreen
        t={t}
        isRTL={isRTL}
        onDone={() => { markOnboardingComplete(); setStep("features"); }}
        onLogin={() => nav("/login")}
      />
    );
  }

  // ------------------------------------------------------------------
  // Screen: Features & Benefits preview (native only — LandingV2 shows
  // the same content on the web landing so we don't show it there).
  // ------------------------------------------------------------------
  if (step === "features") {
    return <OnboardingFeaturesScreen lang={lang} onContinue={() => setStep("competitors")} />;
  }

  // ------------------------------------------------------------------
  // Screen: Competitors comparison ("Features you won't find in other
  // apps"). Never names any competitor app — owner requirement.
  // Flow: competitors → /quiz (testimonials moved AFTER results per
  // owner Sep 30 2026, so it's now the final marketing beat before
  // Create-Your-Account).
  // ------------------------------------------------------------------
  if (step === "competitors") {
    return <OnboardingCompetitorsScreen lang={lang} onContinue={() => nav("/quiz")} />;
  }

  // ------------------------------------------------------------------
  // Screen: Customer testimonials — 5-star reviews from real members.
  // Placed RIGHT before the signup form (i.e. after results/paywall)
  // per owner request Sep 30 2026 as the last trust nudge before the
  // user creates their account.
  // ------------------------------------------------------------------
  if (step === "testimonials") {
    return <OnboardingTestimonialsScreen lang={lang} onContinue={() => setStep("signup")} />;
  }

  // ------------------------------------------------------------------
  // Screen: 3-day free trial promise. Shown ONCE right after the user
  // completes signup and BEFORE they hit the native paywall — the last
  // reassurance beat before the App Store subscribe sheet.
  //
  // Android variant: Google Play IAP is paused while the tax form is
  // pending, so Android users route to Stripe web checkout. We swap in
  // the N10 promo banner (`OnboardingAndroidPromoScreen`) because
  // Stripe has no 3-day free trial — the discount is the hook instead.
  // ------------------------------------------------------------------
  if (step === "trial-promise") {
    const platform = (() => { try { return Capacitor.getPlatform(); } catch { return "ios"; } })();
    // Does this device route to Stripe (not Apple IAP)? Android
    // always does; iOS does only when SubscriptionGate's cached
    // GeoIP decision already came back as "stripe" (i.e. a GCC-6
    // user). iOS users anywhere else go through Apple IAP and get
    // the standard 3-day free-trial promise screen.
    const stripeFlow = platform === "web" || platform === "android" || (() => {
      if (platform !== "ios") return false;
      try {
        const raw = localStorage.getItem("calofay:gcc_gateway_cache_v1");
        if (!raw) return false;
        const parsed = JSON.parse(raw);
        return parsed && parsed.gateway === "stripe";
      } catch { return false; }
    })();

    if (stripeFlow) {
      // Sub-step inside the trial-promise step: show the Guarantee
      // screen first, then the N10 Promo screen, then jump straight
      // into the embedded Stripe checkout with the coupon pre-applied.
      // Owner's requested sequence: Guarantee → Code → Plans.
      if (promoSubStep === "guarantee") {
        return (
          <OnboardingGuaranteeScreen
            lang={lang}
            onContinue={() => setPromoSubStep("water")}
          />
        );
      }
      if (promoSubStep === "water") {
        return <OnboardingWaterCompareScreen lang={lang} onContinue={() => setPromoSubStep("code")} />;
      }
      return (
        <OnboardingAndroidPromoScreen
          lang={lang}
          onContinue={() => {
            const code = platform === "android" ? "CC10" : platform === "web" ? "NEW10" : "N10";
            try { localStorage.setItem("calofay_affiliate_code", code); } catch (_) {}
            if (platform === "web") {
              // Keep any query a direct checkout link carried (ad coupon, plan…).
              let qs = "";
              try { qs = sessionStorage.getItem("calofay_checkout_qs") || ""; sessionStorage.removeItem("calofay_checkout_qs"); } catch (_) {}
              nav(`/checkout?${qs || `coupon=${code}`}`, { replace: true });
              return;
            }
            // iOS-GCC gets `src=ios_gcc&plan=annual` so Checkout.jsx
            // (a) allows iOS native through, (b) auto-skips Step 1
            // for the already-authenticated user, and (c) renders
            // the one-tap Apple Pay Express Element directly.
            const route = platform === "ios"
              ? `/checkout?coupon=${code}&skip_lead=1&src=ios_gcc&plan=annual`
              : `/checkout?coupon=${code}&skip_lead=1`;
            nav(route, { replace: true });
          }}
        />
      );
    }
    if (promoSubStep !== "trial") {
      return <OnboardingWaterCompareScreen lang={lang} onContinue={() => setPromoSubStep("trial")} />;
    }
    return (
      <OnboardingTrialPromiseScreen
        lang={lang}
        onContinue={() => nav("/app", { replace: true })}
      />
    );
  }

  // ------------------------------------------------------------------
  // Screen: Signup
  //
  // Reached in two ways:
  //   * From the slides via legacy path (state-machine `signup` step)
  //   * From /ios/signup route after the user completed the quiz
  //     anonymously — at which point localStorage.calofay_quiz holds
  //     the answers we POST to /api/onboarding/save once the account
  //     is created.
  // ------------------------------------------------------------------
  return (
    <SignupScreen
      t={t}
      isRTL={isRTL}
      onBack={() => (isSignupRoute ? nav(isNativeApp() ? "/results" : "/") : setStep("slides"))}
      onSuccess={async () => {
        markOnboardingComplete();
        try {
          const raw = localStorage.getItem("calofay_quiz");
          const answers = raw ? JSON.parse(raw) : null;
          if (answers && Object.keys(answers).length > 0) {
            await api
              .post("/onboarding/save", { step: 1, data: answers, completed: false })
              .catch(() => {});
          }
        } catch (_) {}
        try { await refresh(); } catch (_) {}
        // Owner (Sep 30 2026): show the 3-day free-trial promise as
        // the last trust beat before the paywall on native (iOS +
        // Android) only. On web the funnel goes straight to /app —
        // web uses Stripe with a 15-day money-back guarantee, not the
        // App Store 3-day trial, so the promise screen would be
        // misleading there.
        setStep("trial-promise");
      }}
    />
  );
}

// ---------------------------------------------------------------------
// Slides sub-component
// ---------------------------------------------------------------------

// Map string icon names (from the server-driven config) back to Lucide
// components. Fallback to Sparkles for unknown icons so a typo in Mongo
// doesn't crash the app. To offer a new icon: add it to the imports and
// register it here — one code change (per icon), then the slide
// content itself can be tweaked freely from the DB.
const SLIDE_ICONS = {
  camera: Camera, brain: Brain, trophy: Trophy, sparkles: Sparkles,
  zap: Zap, heart: Heart, star: Star, award: Award,
  utensils: Utensils, activity: Activity, target: Target,
};

// Fallback slides used when the API is unreachable (offline / first
// launch before the seed runs / server down). Matches the DEFAULT_SLIDES
// Fallback slides used when the API is unreachable (offline / first
// launch before the seed runs / server down). Matches the DEFAULT_SLIDES
// on the backend so users get a consistent experience either way.
// v2 onboarding: hero-image slides. `image` is a path relative to the
// frontend origin — served from `/frontend/public/onboarding/*.webp`.
// All 10 slides have a language-specific EN photoshoot at
// `/onboarding/en/N.webp` (internationally-representative models,
// English-UI mockups) that overrides the AR-market Saudi shoot when
// the user selects English. WebP hero photos are ~30-60KB each — the
// PNG originals used to be 400-550KB each and caused visible white
// squares on iOS Safari while loading (see PRD Session C).
function fallbackSlides(t, lang) {
  const en = lang === "en";
  const src = (n) => (en ? `/onboarding/en/${n}.webp` : `/onboarding/${n}.webp`);
  return [
    { image: src(1),  tint: "from-orange-400 to-orange-600",  title: t.slide1Title,  body: t.slide1Body },
    { image: src(2),  tint: "from-brand-400 to-brand-600",    title: t.slide2Title,  body: t.slide2Body },
    { image: src(3),  tint: "from-emerald-400 to-teal-500",   title: t.slide3Title,  body: t.slide3Body },
    { image: src(4),  tint: "from-purple-400 to-pink-500",    title: t.slide4Title,  body: t.slide4Body },
    { image: src(5),  tint: "from-amber-400 to-orange-500",   title: t.slide5Title,  body: t.slide5Body },
    { image: src(6),  tint: "from-orange-400 to-emerald-500", title: t.slide6Title,  body: t.slide6Body },
    { image: src(7),  tint: "from-brand-400 to-pink-500",     title: t.slide7Title,  body: t.slide7Body },
    { image: src(8),  tint: "from-purple-400 to-brand-500",   title: t.slide8Title,  body: t.slide8Body },
    { image: src(9),  tint: "from-emerald-400 to-teal-500",   title: t.slide9Title,  body: t.slide9Body },
    { image: src(10), tint: "from-orange-400 to-orange-600",  title: t.slide10Title, body: t.slide10Body },
  ];
}

function SlidesScreen({ t, isRTL, onDone, onLogin }) {
  const lang = isRTL ? "ar" : "en";
  const [idx, setIdx] = useState(0);
  const [slides, setSlides] = useState(() => fallbackSlides(t, lang));

  // Fetch server-driven slides on mount; on any failure we silently
  // keep the fallback so onboarding never breaks. Public endpoint —
  // no auth needed (this screen shows BEFORE signup).
  useEffect(() => {
    let alive = true;
    api.get(`/onboarding/slides?lang=${lang}`)
      .then((r) => {
        const list = r?.data?.slides;
        if (alive && Array.isArray(list) && list.length > 0) setSlides(list);
      })
      .catch(() => { /* keep fallback */ });
    return () => { alive = false; };
  }, [lang, t]);

  // Aggressive preloading — the moment the current slide renders,
  // kick off <img> requests for the next 2 slides so tapping Continue
  // feels instant even on 3G / slow iOS Safari. WebP hero photos are
  // ~35KB each so the total prefetch is negligible; without this the
  // user watches a white square for 1-2s on each slide change.
  useEffect(() => {
    if (!slides || slides.length === 0) return;
    for (let offset = 1; offset <= 2; offset++) {
      const nextIdx = idx + offset;
      if (nextIdx >= slides.length) break;
      const src = slides[nextIdx]?.image;
      if (!src) continue;
      const img = new Image();
      img.src = src;
    }
  }, [idx, slides]);

  const isLast = idx === slides.length - 1;
  const current = slides[idx] || slides[0];
  const Icon = SLIDE_ICONS[current.icon] || Sparkles;
  const hasImage = !!current.image;

  // Compact single-viewport layout — every element must fit within
  // 100dvh so users never scroll to see the "I have an account" link.
  // Verified on iPhone SE 375×667 (smallest supported device).
  return (
    <div
      className="always-light bg-white flex flex-col overflow-hidden"
      style={{ height: "calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom))" }}
      data-testid="ios-onboard-slides"
    >
      <div className="max-w-md w-full mx-auto flex justify-end px-5" style={{ paddingTop: "12px" }}>
        <button onClick={onDone} className="text-sm text-neutral-500 px-3 py-1.5 rounded-full hover:bg-neutral-100 transition-colors" data-testid="ios-slides-skip">
          {t.skip}
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6 text-center min-h-0 max-w-md w-full mx-auto">
        {hasImage ? (
          <div className="w-full max-w-[320px] aspect-square rounded-3xl overflow-hidden shadow-floating mb-5 bg-neutral-100">
            <img
              src={current.image}
              alt=""
              className="w-full h-full object-cover"
              draggable={false}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              data-testid={`ios-slide-image-${idx}`}
            />
          </div>
        ) : (
          <div className={`w-24 h-24 rounded-3xl bg-gradient-to-br ${current.tint} flex items-center justify-center shadow-floating mb-5`}>
            <Icon className="w-12 h-12 text-white" strokeWidth={1.5} />
          </div>
        )}
        <h2 className="text-2xl font-black text-neutral-900 mb-3 leading-tight px-2">{current.title}</h2>
        <p className="text-sm text-neutral-600 leading-relaxed max-w-sm">{current.body}</p>
      </div>

      {/* Dots */}
      <div className="max-w-md w-full mx-auto flex justify-center gap-2 pb-4">
        {slides.map((_, i) => (
          <div
            key={i}
            className={`h-2 rounded-full transition-all ${i === idx ? "w-8 bg-brand-500" : "w-2 bg-neutral-300"}`}
          />
        ))}
      </div>

      <div className="max-w-md w-full mx-auto px-6 space-y-2" style={{ paddingBottom: "16px" }}>
        <button
          onClick={() => (isLast ? onDone() : setIdx(idx + 1))}
          className="w-full orange-gradient text-white font-bold py-3.5 rounded-full shadow-floating active:scale-[0.98] transition-transform text-base flex items-center justify-center gap-2"
          data-testid="ios-slides-next"
        >
          {isLast ? t.getStarted : t.continue}
          {isRTL ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
        </button>
        <button onClick={onLogin} className="w-full text-center py-1.5 text-sm text-neutral-600 hover:text-brand-500" data-testid="ios-slides-login">
          {t.haveAccount}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Signup sub-component
// ---------------------------------------------------------------------
function SignupScreen({ t, isRTL, onBack, onSuccess }) {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    if (!email.trim() || password.length < 8) {
      setErr(t.signupError);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      // Existing endpoint. Response sets the auth cookie/token via api.js.
      // We attach the current UI language so `preferred_language` on the
      // new user record starts correct — emails and server-driven
      // localisation follow it from day one instead of defaulting to AR.
      const lang = (typeof window !== "undefined" && localStorage.getItem("calofay:lang")) || "ar";
      const { data: signed } = await api.post("/auth/signup", {
        email: email.trim().toLowerCase(),
        password,
        language: lang,
        // Click IDs + UTMs + Meta cookies captured on the web
        // landing before the user installed the app. Stored on
        // the user doc so (a) admin dashboards can filter by
        // ad campaign and (b) a later server-side Meta CAPI
        // event can look up the saved click IDs for this user.
        attribution: attributionForApi() || undefined,
      });
      // Keep the device signed in (token + device refresh token + remembered creds).
      saveSession(signed);
      saveRememberedCreds(email.trim().toLowerCase(), password);
      onSuccess();
    } catch (e2) {
      const msg = e2?.response?.data?.detail || e2?.message || t.signupError;
      const status = e2?.response?.status;
      // Cross-device rescue: user bought on iOS then reinstalled on
      // Android (or vice-versa), went through onboarding, and typed
      // their existing email. Backend rejects with 400 + "البريد مسجل
      // بالفعل" — instead of showing a scary red error, route them to
      // the Login screen with the email pre-filled and a friendly
      // toast so they realise they already have an account.
      const uiLang = (typeof window !== "undefined" && localStorage.getItem("calofay:lang")) || "ar";
      const isEnLang = uiLang === "en";
      const emailExists =
        status === 400 && typeof msg === "string" && (
          msg.includes("مسجل") ||
          msg.toLowerCase().includes("already") ||
          msg.toLowerCase().includes("exists")
        );
      if (emailExists) {
        toast.info(
          isEnLang
            ? "You already have a Calofay account — please log in."
            : "لديك حساب بالفعل في كالوفاي — سجّل الدخول للمتابعة."
        );
        nav("/login", { state: { email: email.trim().toLowerCase() } });
        return;
      }
      setErr(typeof msg === "string" ? msg : t.signupError);
      toast.error(typeof msg === "string" ? msg : t.signupError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="always-light bg-white flex flex-col overflow-hidden"
      style={{ height: "calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom))" }}
      data-testid="ios-onboard-signup"
    >
      <div className="px-5 pt-5">
        <button onClick={onBack} className="w-9 h-9 rounded-full bg-neutral-100 flex items-center justify-center" data-testid="ios-signup-back">
          {isRTL ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
        </button>
      </div>

      <div className="flex-1 flex flex-col px-6 pt-6">
        <h1 className="text-3xl font-black text-neutral-900 mb-2">{t.signupTitle}</h1>
        <p className="text-sm text-neutral-500 mb-8">{t.signupSubtitle}</p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-neutral-600 mb-1.5">{t.emailLabel}</label>
            <input
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.emailPlaceholder}
              className="w-full rounded-2xl border-2 border-neutral-200 focus:border-brand-500 outline-none px-4 py-3.5 text-base text-neutral-900 bg-white transition-colors"
              data-testid="ios-signup-email"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-neutral-600 mb-1.5">{t.passwordLabel}</label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t.passwordPlaceholder}
                minLength={8}
                className="w-full rounded-2xl border-2 border-neutral-200 focus:border-brand-500 outline-none px-4 py-3.5 pe-12 text-base text-neutral-900 bg-white transition-colors"
                data-testid="ios-signup-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute top-1/2 -translate-y-1/2 end-3 text-neutral-400 hover:text-neutral-600 p-1"
                data-testid="ios-signup-toggle-pw"
                tabIndex={-1}
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {err && (
            <div className="text-xs text-red-600 text-center" data-testid="ios-signup-error">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full orange-gradient text-white font-bold py-4 rounded-full shadow-floating active:scale-[0.98] transition-transform text-base flex items-center justify-center gap-2 disabled:opacity-70"
            data-testid="ios-signup-submit"
          >
            {busy ? "..." : (<>{t.signupCta}{isRTL ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}</>)}
          </button>

          <p className="text-[11px] text-center text-neutral-400 leading-relaxed pt-1" data-testid="ios-signup-terms">
            {isRTL
              ? "بمتابعتك، أنت توافق على "
              : "By continuing you agree to our "}
            <button
              type="button"
              onClick={() => openExternal("https://calofay.app/terms")}
              className="text-brand-500 underline"
              data-testid="ios-signup-tos-link"
            >
              {isRTL ? "شروط الخدمة" : "Terms of Service"}
            </button>
            {isRTL ? " و " : " and "}
            <button
              type="button"
              onClick={() => openExternal("https://calofay.app/privacy")}
              className="text-brand-500 underline"
              data-testid="ios-signup-privacy-link"
            >
              {isRTL ? "سياسة الخصوصية" : "Privacy Policy"}
            </button>
            {isRTL
              ? "، بما في ذلك استخدامنا لخدمات ذكاء اصطناعي من طرف ثالث (OpenAI و Google Gemini) لتحليل صور الوجبات وتوليد ردود المدرّب."
              : ", including our use of third-party AI services (OpenAI and Google Gemini) to analyze meal photos and generate coach responses."}
          </p>
        </form>

        <div className="mt-auto pt-6 pb-6 text-center text-sm text-neutral-600">
          {t.alreadyHaveAccount}{" "}
          <Link to="/login" className="text-brand-500 font-semibold" data-testid="ios-signup-login-link">
            {t.loginLink}
          </Link>
        </div>
      </div>
    </div>
  );
}
